// src/SideWinder.Web/src/composables/useFundEnablement.ts
//
// Singleton composable over /api/auth/me's fund fields, cached per signed-in user.
//
// History: Plan 6b F2 (jhEnabled), gold-productionization Task 10 (goldEnabled), multi-strategy
// allocation Task 4 (allocations), Japan Phase 2 Task 11 (entitledFunds — null while not loaded or
// after an error, never [], so gated funds fail CLOSED), superuser view-only access
// (viewableFunds, same fail-closed semantics), Japan fund page final-review I2 (re-fetch on a user
// change, fail-closed while reloading).
//
// Spec 2026-09-30 per-user fund access: jhEnabled/goldEnabled are gone. hasAccess(fund) is the one
// per-fund predicate: live funds (BGA/JH/GOLD/GGE) follow /me.accessibleFunds and FAIL OPEN while it
// is null (not loaded, error, or a stale API that predates the field — the server fails open too);
// every fund also passes the entitlement predicate, so JAPAN stays hidden unless entitled.

import { ref, computed, watch, effectScope, isRef, type EffectScope } from 'vue'
import { api, type FundAllocation } from '../api/client'
import { isFundVisibleFor, isLiveFundAccessibleFor } from '../components/nav/fundConfig'
import { useAuth } from './useAuth'

// null = /me not fetched yet (or reset); true = a result (or the fail-safe default) is held.
const loaded = ref<boolean | null>(null)
const allocations = ref<FundAllocation[]>([])
const entitledFunds = ref<string[] | null>(null)
const viewableFunds = ref<string[] | null>(null)
const accessibleFunds = ref<string[] | null>(null)
const isLoading = ref(false)
let fetchPromise: Promise<void> | null = null
// userId the currently-held (or in-flight) /me result belongs to; null when
// the last /me failed (401) or nobody is signed in.
let loadedForUserId: string | null = null
// Bumped on every reset so a stale in-flight /me (previous user) is dropped.
let generation = 0
let authScope: EffectScope | null = null

function resetToFailClosed() {
  generation++
  loaded.value = null
  allocations.value = []
  entitledFunds.value = null
  viewableFunds.value = null
  accessibleFunds.value = null
  isLoading.value = false
  fetchPromise = null
  loadedForUserId = null
}

async function fetchEnablement(): Promise<void> {
  if (loaded.value !== null) return
  if (fetchPromise) return fetchPromise
  isLoading.value = true
  const gen = generation
  fetchPromise = (async () => {
    try {
      const me = await api.authMe()
      if (gen !== generation) return
      allocations.value = me.allocations ?? []
      entitledFunds.value = me.entitledFunds ?? null
      viewableFunds.value = me.viewableFunds ?? null
      accessibleFunds.value = me.accessibleFunds ?? null
      loadedForUserId = me.userId ?? null
      loaded.value = true
    } catch {
      if (gen !== generation) return
      // Network error or 401 — gated funds hidden, live funds fail open; composable stays inert.
      allocations.value = []
      entitledFunds.value = null
      viewableFunds.value = null
      accessibleFunds.value = null
      loadedForUserId = null
      loaded.value = true
    } finally {
      if (gen === generation) isLoading.value = false
    }
  })()
  return fetchPromise
}

/**
 * Install (once) a detached watcher on the signed-in user. Detached scope so
 * the watcher survives the unmount of whichever component first called
 * useFundEnablement(). Defensive against test doubles of useAuth that omit
 * `user`.
 */
function ensureAuthWatcher() {
  if (authScope) return
  const auth = useAuth() as Partial<ReturnType<typeof useAuth>>
  const user = auth?.user
  if (!isRef(user)) return
  authScope = effectScope(true)
  authScope.run(() => {
    watch(
      () => user.value?.userId ?? null,
      (userId) => {
        if (userId === null) {
          // Sign-out (or session lost): fail closed; no /me (it would 401).
          resetToFailClosed()
          // Mark as "loaded, nobody" so useFundEnablement() doesn't re-fire /me.
          loaded.value = true
          return
        }
        if (userId === loadedForUserId) return
        resetToFailClosed()
        void fetchEnablement()
      },
    )
  })
}

/** Shared entitlement predicate — see isFundVisibleFor. Reactive (reads the ref). */
function isFundVisible(fund: string): boolean {
  return isFundVisibleFor(entitledFunds.value, fund)
}

/** View rule (entitled, or a superuser's read-only preview of a gated fund): /me.viewableFunds when
 *  present, else entitledFunds — the same source useVisibleFunds uses for nav, through the one
 *  isFundVisibleFor rule, so null (not loaded / error) ⇒ gated hidden. Reactive (reads the refs). */
function canView(fund: string): boolean {
  return isFundVisibleFor(viewableFunds.value ?? entitledFunds.value, fund)
}

/** Spec 2026-09-30: may the caller have `fund`? Live funds: not restricted (fail OPEN while unknown);
 *  every fund: the entitlement predicate too. Reactive (reads the refs). */
function hasAccess(fund: string): boolean {
  return isLiveFundAccessibleFor(accessibleFunds.value, fund) && isFundVisible(fund)
}

/**
 * 2026-11-02: resolves once /me has been answered for the current user (a result,
 * the fail-safe default after an error, or "nobody signed in"). Polls that hit a
 * per-user fund route await this before their FIRST request so a BGA/GOLD-
 * restricted viewer never sends the fail-open first tick (one 403 per gated poll
 * per page load). Resolves immediately when already loaded; re-arms after a
 * user change because resetToFailClosed() puts loaded back to null.
 */
function whenLoaded(): Promise<void> {
  if (loaded.value === true) return Promise.resolve()
  return new Promise<void>(resolve => {
    const stop = watch(loaded, v => {
      if (v === true) { stop(); resolve() }
    })
  })
}

export function useFundEnablement() {
  ensureAuthWatcher()
  if (loaded.value === null && !fetchPromise) {
    void fetchEnablement()
  }
  return {
    allocations: computed(() => allocations.value),
    entitledFunds: computed(() => entitledFunds.value),
    viewableFunds: computed(() => viewableFunds.value),
    accessibleFunds: computed(() => accessibleFunds.value),
    isFundVisible,
    canView,
    hasAccess,
    whenLoaded,
    isLoading: computed(() => isLoading.value),
  }
}

/** Test-only: reset singleton state between test cases. */
export function __resetFundEnablementForTests() {
  authScope?.stop()
  authScope = null
  resetToFailClosed()
}
